import { useGetMachineAgentId, useRoomAgent } from '#~/agent/components/bridge/hooks.js';
import { useIsEmbeddedBridgeOnly } from '#~/agent/hooks.js';
import { clear, post } from '#~/notifications/utils.js';
import { useSelectedVideo } from '#~/room/hooks.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { usePopupStore } from '#~/store/index.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { useDialog, type Api, type UseDialog } from '@studio/design-system/dialog';
import { motion } from '@studio/design-system/motion';
import { usePrompt } from '@studio/design-system/prompt';
import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { StatusEgressState, StatusEgressReason } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement
} from 'react';
import { twMerge } from 'cn';
import { Button } from './button.js';
import { useEarlyAccessModal } from './early-access/index.js';
import {
  isOutputting as isNDIOutputtingFn,
  isRTMPOutputting as isRTMPOutputtingFn,
  useOutputting
} from './output/use-outputting.js';
import { Panel } from './panel/index.js';
import { useRecording } from './recording/index.js';
import type { Props } from './types.js';
import { useNDIOutputNotices } from './use-ndi-output-notices.js';
import { useRecordingDiskNotices, useRecordingDiskSpace } from './use-recording-disk-space.js';

/** `<Production>` component. */
export const Production = ({ className, canControl = false, ...props }: Props): ReactElement => {
  const dialog = useDialog(ProductionDialog);
  const openOutputSettings = useCallback(
    (deviceId: Device['id'], outputAgentId: Agent['id']) => {
      if (!canControl) return;
      usePopupStore.getState().setPopup(false);
      dialog.open({
        defaultTab: 'output',
        defaultOutputTab: 'ndi-output',
        agentId: outputAgentId,
        selectedVideoDevice: deviceId
      });
    },
    [canControl, dialog]
  );
  useNDIOutputNotices(canControl, openOutputSettings);
  useRecordingDiskNotices();

  useLayoutEffect(() => {
    if (!canControl) dialog.close();
  }, [canControl, dialog]);

  // Keep the observer mounted across role changes without conditionally running
  // the control surface's hooks or replaying an already-visible warning.
  return canControl ? (
    <ProductionControls dialog={dialog} className={className} {...props} />
  ) : (
    <></>
  );
};

const ProductionControls = ({
  dialog,
  className,
  ...props
}: Omit<Props, 'canControl'> & {
  dialog: UseDialog<ComponentProps<typeof ProductionDialog>>;
}): ReactElement => {
  const diskSpace = useRecordingDiskSpace();
  const recording = useRecording();
  const { devices, stopAllOutput } = useOutputting();

  const selectedVideoDevice = useSelectedVideo();

  const { agentId: machineAgentId } = useGetMachineAgentId();
  const roomAgent = useRoomAgent(machineAgentId);
  const agentId = roomAgent?.agent?.id;

  const isEmbeddedBridgeOnly = useIsEmbeddedBridgeOnly();
  const isEmbeddedContext = roomAgent?.agent?.embedded ?? isEmbeddedBridgeOnly;

  const recordingEarlyAccess = useEarlyAccessModal('recording');
  const outputEarlyAccess = useEarlyAccessModal('output');

  const hasRecordingEarlyAccess = recordingEarlyAccess.isEnabled;
  const hasOutputEarlyAccess = outputEarlyAccess.isEnabled;

  const showRecordingButton = !isEmbeddedContext;
  const showOutputButton = !isEmbeddedContext;

  const isOutputting = Object.values(devices).some(
    ({ egress }) => isNDIOutputtingFn(egress) || isRTMPOutputtingFn(egress)
  );

  const { recordingDevices, stopRecording } = recording;
  const { setPopup } = usePopupStore();

  const open = (tab: 'output' | 'recording') => () => {
    setPopup(false);
    dialog.open({ defaultTab: tab, agentId, selectedVideoDevice: selectedVideoDevice[0] });
  };

  const prompt = usePrompt();

  const [prevMap, setPrevMap] = useState<Record<string, DeviceEgressSelectorType>>({});

  const recordingFailures = useRef(new Map<string, ReturnType<typeof post>[]>());

  useEffect(() => {
    Object.entries(prevMap).forEach(([deviceId]) => {
      const prev = prevMap[deviceId];
      const current = devices[deviceId as Device['id']];

      if (!prev || !current) return;
      const prevRecordingState = prev.egress?.recordingStatus?.state;
      const currentRecordingState = current.egress?.recordingStatus?.state;

      const prevRTMPDestinations = prev.egress?.rtmpStatus?.destinations ?? {};
      const currentRTMPDestinations = current.egress?.rtmpStatus?.destinations ?? {};

      const ACTIVE = StatusEgressState.EGRESS_STATE_ACTIVE;
      const FAILED = StatusEgressState.EGRESS_STATE_FAILED;
      const FAILED_TO_START = StatusEgressState.EGRESS_STATE_FAILED_TO_START;
      const INACTIVE = StatusEgressState.EGRESS_STATE_INACTIVE;

      if (prevRecordingState !== ACTIVE && currentRecordingState === ACTIVE) {
        recordingFailures.current.delete(deviceId);
        post({
          title: 'Recording started',
          message: `${current.name} is now being recorded.`,
          severity: 'success'
        });
      }
      const diskFull =
        diskSpace.allowed &&
        current.egress?.recordingStatus?.reason === StatusEgressReason.EGRESS_REASON_DISK_FULL;
      if (diskFull) {
        // A retried stop can first report OTHER, then confirm DISK_FULL. Replace
        // only failures from this attempt, preserving unrelated notification history.
        for (const id of recordingFailures.current.get(deviceId) ?? []) clear(id);
        recordingFailures.current.delete(deviceId);
      }
      if (!diskFull && prevRecordingState !== FAILED && currentRecordingState === FAILED) {
        const backendError = current.egress?.recordingStatus?.error;
        const noticeId = post({
          actionable: true,
          title: 'Recording failed',
          message: backendError
            ? `${current.name}: ${backendError}`
            : `${current.name} unexpectedly stopped recording. Go to Recording Settings to restart your recording.`,
          severity: 'error'
        });
        if (diskSpace.allowed)
          recordingFailures.current.set(deviceId, [
            ...(recordingFailures.current.get(deviceId) ?? []),
            noticeId
          ]);
      }
      if (
        !diskFull &&
        prevRecordingState !== FAILED_TO_START &&
        currentRecordingState === FAILED_TO_START
      ) {
        const backendError = current.egress?.recordingStatus?.error;
        const noticeId = post({
          actionable: true,
          title: 'Recording failed to start',
          message: backendError
            ? `${current.name}: ${backendError}`
            : `${current.name} failed to start recording. Go to Recording Settings and try recording again..`,
          severity: 'error'
        });
        if (diskSpace.allowed)
          recordingFailures.current.set(deviceId, [
            ...(recordingFailures.current.get(deviceId) ?? []),
            noticeId
          ]);
      }

      const destinationIds = new Set([
        ...Object.keys(prevRTMPDestinations),
        ...Object.keys(currentRTMPDestinations)
      ]);

      destinationIds.forEach((destinationId) => {
        const prevDestination = prevRTMPDestinations[destinationId];
        const currentDestination = currentRTMPDestinations[destinationId];

        const prevState = prevDestination?.state;
        const currentState = currentDestination?.state;

        if (prevState === undefined && currentState === undefined) return;

        const destinationName =
          currentDestination?.nickName ??
          prevDestination?.nickName ??
          currentDestination?.url ??
          prevDestination?.url ??
          destinationId;

        if (prevState !== FAILED && currentState === FAILED) {
          const backendError = currentDestination?.error;
          post({
            actionable: true,
            title: 'RTMP streaming failed',
            message: backendError
              ? `${destinationName}: ${backendError}`
              : `${destinationName} unexpectedly stopped streaming.`,
            severity: 'error'
          });
          return;
        }

        if (prevState !== FAILED_TO_START && currentState === FAILED_TO_START) {
          const backendError = currentDestination?.error;
          post({
            actionable: true,
            title: 'RTMP failed to start',
            message: backendError
              ? `${destinationName}: ${backendError}`
              : `Verify your RTMP server URL and stream key for ${destinationName}.`,
            severity: 'error'
          });
          return;
        }

        if (prevState !== ACTIVE && currentState === ACTIVE) {
          post({
            title: 'RTMP streaming started',
            message: `Streaming to ${destinationName}.`,
            severity: 'success'
          });
          return;
        }

        if (
          prevState !== undefined &&
          prevState !== INACTIVE &&
          prevState !== FAILED_TO_START &&
          currentState === INACTIVE
        ) {
          post({
            title: 'RTMP streaming stopped',
            message: `Stopped streaming to ${destinationName}.`,
            severity: 'success'
          });
        }
      });
    });

    setPrevMap(devices);
  }, [devices, diskSpace.allowed]);

  const [unsafeIsStoppingOutput, setUnsafeIsStoppingOutput] = useState(false);
  const [unsafeIsStoppingRecording, setUnsafeIsStoppingRecording] = useState(false);

  const isStoppingOutput = isOutputting && unsafeIsStoppingOutput;
  const isStoppingRecording = recordingDevices.length > 0 && unsafeIsStoppingRecording;
  const outputLabel = isStoppingOutput
    ? 'Stopping...'
    : isOutputting
      ? 'Stop Output'
      : 'Start Output';
  /**
   * Regarding the logic in using `setTimeout()` for the output and recording buttons:
   * This logic was added @see: https://github.com/tryiris-ai/studio.client/commit/d30e4df305f7dc788523243927852357224c8eff
   * with this commit message:
   * > Fix crasher when you have multiple audio and video devices added in a
   *   studio. Fix the output / recording start / stop all buttons.
   */
  const diskBlockedReason =
    recordingDevices.length === 0 &&
    recording.sources.length > 0 &&
    recording.sources.every((source) => diskSpace.blockedReasonByDevice[source.id])
      ? [
          ...new Set(recording.sources.map((source) => diskSpace.blockedReasonByDevice[source.id]))
        ].join('. ')
      : undefined;

  // Recording button - show with tooltip when early access not enabled
  const recordingButton = (
    <Button
      labelSTag={STagNew(Sel.ProductionRecording, 'primary-action')}
      settingsSTag={STagNew(Sel.ProductionRecording, 'settings')}
      disabled={!!diskBlockedReason}
      onSettings={!hasRecordingEarlyAccess ? recordingEarlyAccess.open : open('recording')}
      label={
        isStoppingRecording
          ? 'Stopping...'
          : recordingDevices.length > 0
            ? 'Stop Recording'
            : 'Start Recording'
      }
      onLabel={
        hasRecordingEarlyAccess
          ? () => {
              if (recordingDevices.length > 0) {
                stopRecording(() => {
                  setUnsafeIsStoppingRecording(true);
                  setTimeout(() => {
                    setUnsafeIsStoppingRecording(false);
                  }, 10_000);
                });
              } else {
                open('recording')();
              }
            }
          : recordingEarlyAccess.open
      }
      state={recordingDevices.length > 0 ? 'in-progress' : 'idle'}
    />
  );

  // Output button - show with tooltip when early access not enabled
  const outputButton = (
    <Button
      labelSTag={STagNew(Sel.ProductionOutput, 'primary-action')}
      onSettings={!hasOutputEarlyAccess ? outputEarlyAccess.open : open('output')}
      label={outputLabel}
      onLabel={
        hasOutputEarlyAccess
          ? () => {
              if (isOutputting) {
                prompt.confirm({
                  title: 'Stop Output',
                  message:
                    'Are you sure you want to stop output? All sources will stop outputting immediately.',
                  okay: {
                    variant: 'danger',
                    label: 'Stop Output'
                  },
                  onConfirm(answer: boolean) {
                    if (answer) {
                      setUnsafeIsStoppingOutput(true);
                      stopAllOutput();
                      setTimeout(() => {
                        setUnsafeIsStoppingOutput(false);
                      }, 10_000);
                    }
                  }
                });
              } else {
                open('output')();
              }
            }
          : outputEarlyAccess.open
      }
      state={isOutputting ? 'in-progress' : 'idle'}
    />
  );

  return (
    <div className={twMerge('flex gap-12', className)} {...props}>
      {showRecordingButton &&
        (diskBlockedReason ? (
          <Tooltip
            zIndex={100}
            trigger={
              <div
                tabIndex={0}
                aria-label={diskBlockedReason}
                className='focus-visible:ring-primary/50 focus-visible:ring-2 [&_button:disabled]:cursor-not-allowed [&_button:disabled]:opacity-50'
                {...STagNew(Sel.ProductionRecording, 'disk-blocked')}
              >
                {recordingButton}
              </div>
            }
          >
            <div className='rounded-4 bg-base-100 px-12 py-8 text-sm text-foreground'>
              {diskBlockedReason}
              <Arrow className='fill-base-100' />
            </div>
          </Tooltip>
        ) : hasRecordingEarlyAccess ? (
          recordingButton
        ) : (
          <Tooltip zIndex={100} trigger={<div className='cursor-pointer'>{recordingButton}</div>}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
            >
              <div className='max-w-[280px] rounded-4 bg-[white] px-12 py-8 text-sm text-black shadow-lg'>
                Recording is available as an Early Access feature. Click to learn more.
                <Arrow className='fill-[white]' />
              </div>
            </motion.div>
          </Tooltip>
        ))}

      {showOutputButton &&
        (hasOutputEarlyAccess ? (
          outputButton
        ) : (
          <Tooltip zIndex={100} trigger={<div className='cursor-pointer'>{outputButton}</div>}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
            >
              <div className='max-w-[280px] rounded-4 bg-[white] px-12 py-8 text-sm text-black shadow-lg'>
                Output (NDI Output and RTMP streaming) is available as an Early Access feature.
                Click to enable it.
                <Arrow className='fill-[white]' />
              </div>
            </motion.div>
          </Tooltip>
        ))}
    </div>
  );
};

export const ProductionDialog = forwardRef<
  Api,
  {
    defaultTab?: 'output' | 'recording';
    defaultOutputTab?: 'ndi-output';
    agentId?: T_Maybe<Agent['id']>;
    selectedVideoDevice?: T_Maybe<Device['id']>;
  }
>(({ defaultTab = 'recording', defaultOutputTab, agentId, selectedVideoDevice }, ref) => (
  <Panel
    defaultTab={defaultTab}
    defaultOutputTab={defaultOutputTab}
    ref={ref}
    agentId={agentId}
    selectedVideoDevice={selectedVideoDevice}
  />
));
